// Interfaces e tipos para reconhecimento facial

export interface FacialUserData {
  localId: string;
  userId: string;
  cardId: string;
  tag?: string; // Tag do cartão/pulseira (pode ser o mesmo que cardId)
  studentName: string;
  nickname: string;
  balance: number;
  dailyLimit: number;
  dailyLimitEnabled: boolean;
  minBalance: number;
  minBalanceEnabled: boolean;
  password: string;
  status: number;
  locationName: string;
  localClienteId: string;
  faceRecognitionEnabled: boolean;
  restrictedItems: any[];
  secondCardRequested: boolean;
}

export interface FacialCaptureResult {
  path: string;           // Caminho da imagem (com file://)
  imageBase64: string;    // Imagem em base64
  width: number;          // Largura em pixels
  height: number;         // Altura em pixels
  format: string;         // Formato da imagem (JPEG/PNG)
}

export interface CaptureAndIdentifyParams {
  autoCapture?: boolean;  // Se true, captura automaticamente ao detectar rosto (padrão: true)
  personGroupId: string;  // ID do grupo de pessoas (obrigatório)
}

export interface IdentifyFromImageParams {
  file: {
    uri: string;      // URI do arquivo (ex: "file:///path/to/image.jpg")
    name: string;     // Nome do arquivo
    type: string;     // Tipo MIME (ex: "image/jpeg")
  };
  personGroupId: string;  // ID do grupo de pessoas
}

export interface CaptureAndIdentifyResponse {
  success: boolean;
  isIdentified: boolean;
  personId?: string;
  personName?: string;
  userData?: FacialUserData | null;
  userDataRaw?: string;  // JSON stringificado
  confidence?: number;
  message: string;
  /** Lista de rostos similares identificados (quando disponível) */
  similarFaces?: SimilarFace[];
  captureInfo?: {
    width: number;
    height: number;
    format: string;
  };
  error?: string;
}

export interface SimilarFace {
  personId: string;
  personName: string;
  userData: string;  // JSON stringificado
  confidence: number;
}

export interface IdentifyFromImageResponse {
  success: boolean;
  isIdentified: boolean;
  personId?: string;
  personName?: string;
  userData?: FacialUserData | null;
  userDataRaw?: string;
  confidence?: number;
  message: string;
  /** Lista de rostos similares identificados (quando disponível) */
  similarFaces?: SimilarFace[];
  error?: string;
}

export interface IsAvailableResponse {
  success: boolean;
  available: boolean;
  error?: string;
}

export interface CameraPermissionResponse {
  success: boolean;
  hasPermission?: boolean;
  granted?: boolean;
  error?: string;
}

/**
 * Captura uma imagem facial e identifica a pessoa automaticamente
 * @param params Parâmetros de captura e identificação
 * @returns Resultado da captura e identificação
 */
const captureAndIdentify = async (
  params: CaptureAndIdentifyParams
): Promise<CaptureAndIdentifyResponse> => {
  try {
    const result = await window.RNBridge.callNative("captureAndIdentify", {
      autoCapture: params.autoCapture ?? true,
      personGroupId: params.personGroupId,
    });
    return result;
  } catch (error) {
    return {
      success: false,
      isIdentified: false,
      message: error instanceof Error ? error.message : "Erro desconhecido",
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

/**
 * Identifica uma pessoa a partir de uma imagem já capturada (arquivo)
 * @param params Parâmetros de identificação a partir de arquivo
 * @returns Resultado da identificação
 */
const identifyFromImage = async (
  params: IdentifyFromImageParams
): Promise<IdentifyFromImageResponse> => {
  try {
    const result = await window.RNBridge.callNative("identifyFromImage", {
      file: params.file,
      personGroupId: params.personGroupId,
    });
    return result;
  } catch (error) {
    return {
      success: false,
      isIdentified: false,
      message: error instanceof Error ? error.message : "Erro desconhecido",
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

/**
 * Verifica se o módulo facial está disponível no dispositivo
 * @returns Resultado da verificação de disponibilidade
 */
const isAvailable = async (): Promise<IsAvailableResponse> => {
  try {
    const result = await window.RNBridge.callNative("facialIsAvailable");
    return result;
  } catch (error) {
    return {
      success: false,
      available: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

/**
 * Verifica se a permissão de câmera foi concedida
 * @returns Resultado da verificação de permissão
 */
const checkCameraPermission = async (): Promise<CameraPermissionResponse> => {
  try {
    const result = await window.RNBridge.callNative("checkCameraPermission");
    return result;
  } catch (error) {
    return {
      success: false,
      hasPermission: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

/**
 * Solicita permissão de câmera ao usuário
 * @returns Resultado da solicitação de permissão
 */
const requestCameraPermission = async (): Promise<CameraPermissionResponse> => {
  try {
    const result = await window.RNBridge.callNative("requestCameraPermission");
    return result;
  } catch (error) {
    return {
      success: false,
      granted: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

export {
  captureAndIdentify,
  identifyFromImage,
  isAvailable,
  checkCameraPermission,
  requestCameraPermission,
};
